import { Feature } from 'geojson'

export interface ClientPropertyConfig {
  asset_type: string
  report_hazards: PropertyReportHazards
}

export type PropertySuggestion = {
  asset_id: string
  key: string
  value: string
  search_match: string
}

export interface HazardTechnicalReport {
  title: string
  date: string
  scope: string
  prepared_by: string
  commissioned_by: string
  purpose: string
  url: string
  summary: string
  [key: string]: string
}

export type HazardTechnicalReports = Record<string, HazardTechnicalReport>

export interface PropertyInformationResponse {
  Address: string
  Area?: string
  'LINZ parcel ID'?: number | string
  'Legal description'?: string
  Suburb?: string
  Titles?: string
  'Valuation number'?: string
  [key: string]: string | number | undefined
}

export interface PropertyInformation {
  Address: string
  Area?: string
  LinzParcelId?: string
  LegalDescription?: string
  Suburb?: string
  Titles?: string
  ValuationNumber?: string
  [key: string]: string | number | undefined
}

export const mapPropertyInformationResponse = (
  response: PropertyInformationResponse,
): PropertyInformation => ({
  Address: response.Address,
  Area: response.Area,
  LinzParcelId: response['LINZ parcel ID']?.toString(),
  LegalDescription: response['Legal description'],
  Suburb: response.Suburb,
  Titles: response.Titles,
  ValuationNumber: response['Valuation number'],
  ...Object.fromEntries(
    Object.entries(response).filter(
      ([key]) =>
        ![
          'Address',
          'Area',
          'LINZ parcel ID',
          'Legal description',
          'Suburb',
          'Titles',
          'Valuation number',
        ].includes(key),
    ),
  ),
})

export type ExposureStatus = 'true' | 'false' | 'unknown'

export interface ScenariosByAsset {
  hazard_scenario: string
  is_exposed: ExposureStatus
  hazard_extent_source: 'calculated' | 'authoritative' | 'none'
}

export interface ElementExposure {
  element: string
  percentage: number
  total_metric: number
  exposure: number
  unit: string
}

export interface ValueSet {
  value_set_id: string
  elements: {
    type: string
    weight: number
    label: string
  }[]
}

export interface PropertyMapLayer {
  layer_id: string
  display_name: string
  layer_type: string
}

export interface MCAHazardScenario {
  hazard_uid: string
  scenario_name: string
  display_name: string
}

export interface MCAScenario {
  scenario_id: string
  description: string
  hazard_scenarios: MCAHazardScenario[]
  parameters: {
    [key: string]: string | number | boolean | null
  }
}

export type PropertyReportHazards = {
  group_name: string
  hazards: {
    display_name: string
    scenarios: string[]
    description?: string
    description_header?: string
    layer_ids?: string[]
  }[]
}[]

export interface Assessment {
  assessment_id: string
  description: string
  scenario_ids: string[]
  value_set_config: {
    value_set_weights: { [key: string]: number }
  }
  is_default: boolean
}

export interface PropertyContextProps {
  setSelectedAssetId: (assetId: string | null) => void
  selectedAssetId: string | null
  selectedAssetInfo: PropertyInformation | null
  selectedAssetInfoIsLoading: boolean
  selectedAssetInfoIsInitialized: boolean
  propertyAssetType: string | null
  reportHazards: PropertyReportHazards | null
  technicalReports: HazardTechnicalReports | null
  shownHazardLayers: string[]
  setShownHazardLayers: (layerIds: string[]) => void
  selectedAssetFeature: Feature | null
}

export const propertyColorLegend = {
  'Very high': '#7D0000',
  High: '#F80000',
  Moderate: '#FF7A7B',
  Low: '#FFBBBA',
  'Very low': '#FFDADA',
}
